/**
 * practiceFlow — 스코어링 연습 플로우의 순수 단계(phase) 머신 (#9).
 *
 * 단계: 준비(ready) → 카운트다운(countdown) → 연습중(practicing) → 결과(result).
 * "연습 시작"이 카운트다운(3·2·1)을 열고, 0에 닿으면 연습중으로, 멜로디가 끝나면
 * 결과로 넘어간다. 중단/다시하기/멜로디 변경은 준비로 되돌린다.
 *
 * React·네이티브·벽시계 의존 없음 — 같은 입력은 같은 결과(결정론). 틱(시간 경과)·
 * 세션 start/stop 같은 부수효과는 ScoringPanel이 이벤트로 주입한다. vitest 대상.
 */

export type PracticePhase = 'ready' | 'countdown' | 'practicing' | 'result';

/** 카운트다운 시작 숫자(3 → 2 → 1 → 연습중). */
export const COUNTDOWN_FROM = 3;

export interface PracticeFlowState {
  phase: PracticePhase;
  /** countdown 동안 크게 표시할 현재 숫자(3·2·1). 그 외 단계는 0. */
  count: number;
}

export type PracticeFlowEvent =
  | { type: 'start' } // ready → countdown
  | { type: 'tick' } // countdown 1초 경과 (3→2→1→연습중)
  | { type: 'finish' } // practicing → result (멜로디 끝/세션 정지)
  | { type: 'abort' } // countdown|practicing → ready (중단)
  | { type: 'retry' } // result → ready (같은 멜로디로 다시)
  | { type: 'reset' }; // 어디서든 → ready (멜로디 변경 등)

export const INITIAL_PRACTICE_STATE: PracticeFlowState = {
  phase: 'ready',
  count: 0,
};

/**
 * 단계 전이. 허용되지 않는 전이는 상태를 그대로 둔다(무시) — 비동기 이벤트가
 * 어긋난 단계에서 와도 안전하게 흡수한다.
 */
export const practiceFlowReducer = (
  state: PracticeFlowState,
  event: PracticeFlowEvent,
): PracticeFlowState => {
  switch (event.type) {
    case 'start':
      return state.phase === 'ready'
        ? { phase: 'countdown', count: COUNTDOWN_FROM }
        : state;
    case 'tick': {
      if (state.phase !== 'countdown') {
        return state;
      }
      const next = state.count - 1;
      return next <= 0
        ? { phase: 'practicing', count: 0 }
        : { phase: 'countdown', count: next };
    }
    case 'finish':
      return state.phase === 'practicing'
        ? { phase: 'result', count: 0 }
        : state;
    case 'abort':
      return state.phase === 'countdown' || state.phase === 'practicing'
        ? INITIAL_PRACTICE_STATE
        : state;
    case 'retry':
      return state.phase === 'result' ? INITIAL_PRACTICE_STATE : state;
    case 'reset':
      return INITIAL_PRACTICE_STATE;
  }
};
